import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Copy, MapPin, Plus, Route, Trash2 } from "lucide-react";
import cloneDeep from "lodash/cloneDeep";
import Button from "../../components/Button";
import FormField from "../../components/FormField";
import IconButton from "../../components/IconButton";
import TextInput from "../../components/TextInput";
import LineAtSite from "./LineAtSite";

const firstError = (errors, key) => {
  const value = errors?.[key];
  return Array.isArray(value) ? value[0] || "" : value || "";
};

const Worksite = (props) => {
  const [isReorderingLine, setIsReorderingLine] = useState(false);

  const packData = props.values;
  const worksiteData = props.worksiteData;
  const worksiteIndex = props.worksiteIndex;
  const possessionData = props.possessionData;
  const possessionIndex = props.possessionIndex;
  const valuePath = `possessions.${possessionIndex}.worksites.${worksiteIndex}`;
  const worksiteReferenceError = firstError(
    props.errors,
    `data.${valuePath}.worksite_ref`,
  );
  const allLinesAtSite = packData?.possessions ? packData.possessions.flatMap(possession =>
    (possession?.worksites ?? []).flatMap(worksite => worksite.lines_at_site)
  ) : [];

  useEffect(() => {
    const { level_crossings } = updateAttachedLinesInPossession();
    const forms = updateAttachedLinesInForms();
    props.handleInputChange(`possessions.${possessionIndex}.level_crossings`, level_crossings, { systemSync: true });
    props.handleInputChange('forms', forms, { systemSync: true });
  // Line changes are the trigger; the helpers intentionally read the latest pack snapshot from this render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksiteData?.lines_at_site]);

  const updateAttachedLinesInPossession = () => {
    const linesAtSite = worksiteData?.lines_at_site ?? [];

    const updatedPossession = { ...possessionData };

    if (updatedPossession.level_crossings) {
        updatedPossession.level_crossings.forEach(levelCrossing => {
            if (!levelCrossing.lines || levelCrossing.lines.length === 0) return;

            levelCrossing.lines = levelCrossing.lines.filter((lcLine) => {
                const siteLine = linesAtSite.find(siteLine => siteLine.line_reference === lcLine.line_reference);

                if (siteLine) {
                    if (siteLine.name) {
                        lcLine.name = siteLine.name;
                        return true;
                    }
                    return false;
                }

                return false;
            });
        });
    }

    return updatedPossession;
  };
  
  const updateAttachedLinesInForms = () => {
    const existingPackData = cloneDeep(packData);
  
    if (allLinesAtSite.length === 0) {
      return existingPackData.forms;
    }
  
    const updatedForms = Object.keys(existingPackData.forms).reduce((updatedFormsAcc, formKey) => {
      const form = existingPackData.forms[formKey] || { active : 0 };
  
      if (form.otps) {
        form.otps = form.otps.map(otp => {
          const siteLine = allLinesAtSite.find(line => line.line_reference === otp.line_affected?.line_reference);
          if (siteLine) {
            if (siteLine.name) {
              otp.line_affected.name = siteLine.name;
            } else {
              delete otp.line_affected;
            }
          } else {
            delete otp.line_affected;
          }
          return otp;
        });
      }
  
      if (form.trolleys) {
        form.trolleys = form.trolleys.map(trolley => {
          const siteLine = allLinesAtSite.find(line => line.line_reference === trolley.line_affected?.line_reference);
          if (siteLine) {
            if (siteLine.name) {
              trolley.line_affected.name = siteLine.name;
            } else {
              delete trolley.line_affected;
            }
          } else {
            delete trolley.line_affected;
          }
          return trolley;
        });
      }
  
      if (form.movements) {
        form.movements = form.movements.map(movement => {
          const siteLine = allLinesAtSite.find(line => line.line_reference === movement.line_from_affected?.line_reference);
          if (siteLine) {
            if (siteLine.name) {
              movement.line_from_affected.name = siteLine.name;
            } else {
              delete movement.line_from_affected;
              delete movement.elr_from;
            }
          } else {
            delete movement.line_from_affected;
              delete movement.elr_from;
          }

          const toSiteLine = allLinesAtSite.find(line => line.line_reference === movement.line_to_affected?.line_reference);
          if (toSiteLine) {
            if (toSiteLine.name) {
              movement.line_to_affected.name = toSiteLine.name;
            } else {
              delete movement.line_to_affected;
              delete movement.elr_to;
            }
          } else {
            delete movement.line_to_affected;
            delete movement.elr_to;
          }
          return movement;
        });
      }
  
      updatedFormsAcc[formKey] = form;
      return updatedFormsAcc;
    }, {});
  
    return updatedForms;
  };

  const endReorderLine = (result) => {
    if (!result.destination) return;
    const newItems = Array.from(worksiteData.lines_at_site);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);
    props.handleInputChange(`${valuePath}.lines_at_site`, newItems);
    setIsReorderingLine(false);
  };

  const handleCloneWorksite = () => {
    const existingWorksites = cloneDeep(possessionData?.worksites || []);
    let clonedWorksite = cloneDeep(worksiteData);
    clonedWorksite.worksite_ref = "";
    clonedWorksite?.lines_at_site?.map(line => {
      line.name = "";
      line.line_reference = null;
      return line;
    });

    props.handleInputChange(`possessions.${possessionIndex}.worksites`, [...existingWorksites, clonedWorksite]);
  };

  return (
    <section className="wsm-worksite-editor" id={`worksite-section-${possessionIndex}-${worksiteIndex}`}>
      <div className="wsm-worksite-editor__heading">
        <div className="wsm-worksite-editor__title">
          <span><MapPin aria-hidden="true" /></span>
          <div><h5>Worksite {worksiteIndex + 1}</h5><p>Define the reference and lines at this site.</p></div>
        </div>
        <div className="wsm-item-actions">
          <IconButton aria-label={`Duplicate worksite ${worksiteIndex + 1}`} icon={Copy} onClick={handleCloneWorksite} size="compact" variant="ghost" />
          <IconButton aria-label={`Remove worksite ${worksiteIndex + 1}`} disabled={worksiteIndex === 0} icon={Trash2} onClick={() => props.handleRemoveFormRow(`possessions.${possessionIndex}.worksites`, worksiteIndex, true, "worksite")} size="compact" variant="destructive" />
        </div>
      </div>

      <FormField error={worksiteReferenceError} id={`worksite-ref-${possessionIndex}-${worksiteIndex}`} label="Worksite reference" required>
        {(fieldProps) => <TextInput {...fieldProps} onChange={(event) => props.handleInputChange(`${valuePath}.worksite_ref`, event.target.value)} value={worksiteData.worksite_ref || ""} />}
      </FormField>

      <section className="wsm-line-list">
        <div className="wsm-line-list__heading">
          <div><h5>Lines at site</h5><p>Define the railway lines and mileage limits within this worksite.</p></div>
          <Button icon={Plus} onClick={() => props.handleAddFormRow(`${valuePath}.lines_at_site`, props.linesAtSiteTemplate)} size="compact" variant="outline">Add line</Button>
        </div>
        <DragDropContext onDragStart={() => setIsReorderingLine(true)} onDragEnd={endReorderLine}>
          <Droppable droppableId={`droppable-${worksiteIndex}`}>
            {(provided) => (
              <div className="wsm-line-list__items" {...provided.droppableProps} ref={provided.innerRef}>
                {(worksiteData.lines_at_site || []).map((line, lineIndex) => (
                  <Draggable
                    draggableId={`line-${possessionIndex}-${worksiteIndex}-${lineIndex}`}
                    index={lineIndex}
                    isDragDisabled={(worksiteData.lines_at_site || []).length < 2}
                    key={`line-${possessionIndex}-${worksiteIndex}-${lineIndex}`}
                  >
                    {(dragProvided, snapshot) => (
                      <article className={["wsm-line-editor", isReorderingLine && "wsm-line-editor--reordering", snapshot.isDragging && "wsm-line-editor--dragging"].filter(Boolean).join(" ")} id={`line-section-${possessionIndex}-${worksiteIndex}-${lineIndex}`} ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                        <div className="wsm-line-editor__heading"><span><Route aria-hidden="true" /></span><strong>Line {lineIndex + 1}</strong></div>
                        <LineAtSite {...props} allLinesAtSite={worksiteData.lines_at_site} dragHandleProps={dragProvided.dragHandleProps} key={`line-fields-${lineIndex}`} lineData={line} lineIndex={lineIndex} possessionData={possessionData} possessionIndex={possessionIndex} worksiteData={worksiteData} worksiteIndex={worksiteIndex} />
                      </article>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </section>
    </section>
  );
};

export default Worksite;
